import gsap from 'gsap';

import { parseElementConfig } from '@/js/modules/animate/parse-options';
import { parseRootConfig } from '@/js/modules/animate/parse-root-options';
import { playElementAnimation, prepareElement, revealElement } from '@/js/modules/animate/play';
import { assertAnimationPresets } from '@/js/modules/animate/presets';
import {
  DATA_PLAYED,
  SELECTOR_ROOT,
  SELECTOR_TARGET,
  VIEW_TRIGGER_MARGIN_PERCENT,
} from '@/js/modules/animate/types';

const observedRoots = new WeakSet<HTMLElement>();

// Roots still waiting for their observer to fire, mapped to it. Holding the
// observers here also keeps them alive: one referenced only by its own callback
// may be collected, and the root would then never be revealed.
const pendingRoots = new Map<HTMLElement, IntersectionObserver>();

let visibilityListenerBound = false;

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function canAnimate(): boolean {
  return !prefersReducedMotion();
}

function getTargets(root: HTMLElement): HTMLElement[] {
  return gsap.utils.toArray<HTMLElement>(SELECTOR_TARGET, root)
    .filter(element => !element.hasAttribute(DATA_PLAYED));
}

function playRoot(root: HTMLElement): void {
  const targets = getTargets(root);

  for (const element of targets) {
    const config = parseElementConfig(element);

    if (!config) {
      element.removeAttribute(DATA_PLAYED);
      continue;
    }

    playElementAnimation(element, config);
  }
}

function revealRoot(root: HTMLElement): void {
  for (const element of getTargets(root))
    revealElement(element);
}

function prepareRoot(root: HTMLElement): void {
  for (const element of getTargets(root))
    prepareElement(element);
}

function runRoot(root: HTMLElement): void {
  if (!canAnimate())
    revealRoot(root);
  else
    playRoot(root);
}

function effectiveThreshold(root: HTMLElement, threshold: number): number {
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const rootHeight = root.getBoundingClientRect().height;
  const maxRatio = rootHeight > viewportHeight && rootHeight > 0
    ? (viewportHeight / rootHeight) * 0.98
    : 1;

  return Math.min(threshold, maxRatio);
}

// The ratio the observer would report for this root right now, computed with the
// same vertical geometry (including the bottom trigger margin).
function currentViewRatio(root: HTMLElement): number {
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const rect = root.getBoundingClientRect();

  if (rect.height <= 0)
    return 0;

  const bottomEdge = viewportHeight * (1 + VIEW_TRIGGER_MARGIN_PERCENT / 100);
  const visibleHeight = Math.min(rect.bottom, bottomEdge) - Math.max(rect.top, 0);

  return Math.max(visibleHeight, 0) / rect.height;
}

function releaseRoot(root: HTMLElement): void {
  pendingRoots.get(root)?.unobserve(root);
  pendingRoots.delete(root);
}

function observeRootInView(root: HTMLElement, threshold: number): void {
  const effective = effectiveThreshold(root, threshold);

  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.intersectionRatio < effective)
        continue;

      const target = entry.target as HTMLElement;

      releaseRoot(target);
      runRoot(target);
    }
  }, {
    threshold: effective,
    rootMargin: `0px 0px ${VIEW_TRIGGER_MARGIN_PERCENT}% 0px`,
  });

  pendingRoots.set(root, observer);
  observer.observe(root);
}

// Observer callbacks are not delivered while the document is not rendering, so a
// block loaded in a background tab can stay at its hidden initial state. Re-check
// what is in view as soon as the page becomes visible again.
function playPendingRootsInView(): void {
  for (const root of [...pendingRoots.keys()]) {
    const { threshold } = parseRootConfig(root);

    if (currentViewRatio(root) < effectiveThreshold(root, threshold))
      continue;

    releaseRoot(root);
    runRoot(root);
  }
}

function mountRoot(root: HTMLElement): void {
  if (observedRoots.has(root))
    return;

  observedRoots.add(root);

  if (!canAnimate() || typeof IntersectionObserver === 'undefined') {
    revealRoot(root);
    return;
  }

  prepareRoot(root);

  const { inView, threshold } = parseRootConfig(root);

  if (!inView) {
    runRoot(root);
    return;
  }

  // A root that is already in view on load must not wait for the first observer
  // callback: while the document is not rendering (background tab, throttled
  // paint) that callback never arrives and the block would stay hidden. A fixed
  // header never recovers even later, because scrolling does not change its ratio.
  if (currentViewRatio(root) >= effectiveThreshold(root, threshold))
    runRoot(root);
  else
    observeRootInView(root, threshold);
}

export function initAnimate(): void {
  if (typeof window === 'undefined')
    return;

  assertAnimationPresets();

  if (!visibilityListenerBound) {
    visibilityListenerBound = true;

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible')
        playPendingRootsInView();
    });
  }

  const roots = document.querySelectorAll<HTMLElement>(SELECTOR_ROOT);

  for (const root of roots) {
    try {
      mountRoot(root);
    }
    catch (error) {
      // Content must never stay stuck at its hidden initial state because setup failed.
      releaseRoot(root);
      revealRoot(root);
      console.error('[animate] failed to mount root', error);
    }
  }
}

export { parseElementConfig } from '@/js/modules/animate/parse-options';
export { parseRootConfig } from '@/js/modules/animate/parse-root-options';
export { playElementAnimation, prepareElement, revealElement } from '@/js/modules/animate/play';
export { ANIMATION_PRESETS } from '@/js/modules/animate/presets';
export type { AnimationName, ElementAnimationConfig, RootAnimationConfig } from '@/js/modules/animate/types';
export { ANIMATION_NAMES, isAnimationName } from '@/js/modules/animate/types';
